import { useState } from 'react';
import ResponsePicker from './ResponsePicker';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const PROPERTY_TYPES = [
  'Attached', 'Condominium', 'Detached', 'Hi Rise Apartment', 'Link',
  'Low Rise Apartment', 'Minihome', 'Mobile/Manufactured', 'Multi-family',
  'Row/Townhouse', 'Semi-Detached', 'Stacked Townhouse', 'Triplex', 'Other',
];
const CONSTRUCTION_TYPES = ['Brick', 'Concrete', 'Concrete Block', 'Log', 'Other (specify)', 'Plywood', 'Stone', 'Wood'];
const WINDOW_TYPES       = ['Aluminum', 'Other (specify)', 'PVC', 'PVC Clad Wood', 'Wood'];
const WINDOW_THICKNESS   = ['Double', 'Other (specify)', 'Single', 'Triple'];
const BASEMENT_TYPES     = ['Full', 'Partial', 'Crawl Space', 'Slab on Grade', 'None'];
const FOUNDATION_TYPES   = ['Brick', 'Concrete', 'Concrete Block', 'Insulated Concrete Forms', 'Other', 'Pier', 'Plywood', 'Stone', 'Wood'];
const ROOFING_TYPES      = ['Asphalt/Fiberglass Shingle', 'Clay Tile', 'Metal', 'Other (specify)', 'PVC/Polycarbonate/Plastic', 'Slate', 'Tar & Gravel', 'Wood Shingle/Shake'];
const EXTERIOR_FINISHES  = ['Brick', 'Brick Veneer', 'Hardboard/Fibre cement', 'Log', 'Other (specify)', 'Stucco', 'Stucco & Siding', 'Vinyl/Aluminum', 'Wood Shingle'];
const EXT_CONDITIONS     = ['Good', 'Avg', 'Fair', 'Poor'];
const ENERGY_LABELS      = ['EnerGuide', 'Energy Star', 'LEED', 'Net Zero', 'None', 'Not Applicable', 'Not Provided', 'Other', 'R-2000 Standard'];
const EV_OPTIONS         = ['Level 1 (120V)', 'Level 2 (240V)', 'Level 3 (fast)', 'None', 'Not Applicable'];
const SOLAR_OPTIONS      = ['Leased', 'Leased as reported by owner', 'None', 'Not Applicable', 'Owned', 'Owned as reported by owner', 'Unknown'];
const ELEC_OTHER_OPTS    = ['Aluminum', 'Garage Sub Panel', 'Generator/Portable Service', 'Knob and Tube', 'Other (specify)', 'see photos', 'Service Drop Only', 'Service Panel Outside', 'Solar Panels', 'Splitter Box', 'Splitter Box on Yard Pole'];
const ELEC_OTHER2_OPTS   = ['Not Applicable', 'Off grid', 'Other (specify)', 'See Comments'];
const HEATING_SYSTEMS    = ['Electric Baseboard', 'Fireplace Only', 'Forced Air', 'Geothermal', 'Heat Pump', 'Hot Water/Steam Boiler', 'Hot Water/Steam Radiator', 'Other (SPECIFY)', 'Radiant', 'Solar Heat', 'Wood Pellet Stove', 'Wood Stove'];
const FUEL_TYPES         = ['Electric', 'Ground Source', 'Natural Gas', 'Oil', 'Other (specify)', 'Propane', 'Unknown', 'Wood'];
const WATER_HEATERS      = ['Electric', 'Integrated with boiler', 'Natural Gas', 'None', 'Other (SPECIFY)', 'Propane', 'Solar', 'Tankless/Instant'];
const COOLING_SYSTEMS    = ['Central Air', 'Ductless mini split', 'Geothermal', 'Heat Pump', 'None', 'Other (SPECIFY)', 'Window air conditioner'];
const PLUMBING_LINES     = ['Copper', 'Kitec', 'Mixed', 'Other', 'PEX', 'PEX/PVC', 'Steel', 'Unknown'];
const CEILING_OTHER_OPTS = ['Ceiling Tile', 'None', 'Other', 'Shiplap', 'T-Bar'];
const FLOORING_OPTIONS   = ['Bamboo', 'Carpet', 'Ceramic', 'Concrete', 'Hardwood', 'Laminate', 'Linoleum', 'Marble', 'Other', 'Stone', 'Terrazzo', 'Tile', 'Unfinished', 'Vinyl'];
const FIREPLACE_TYPES    = ['Fireplace (Gas)', 'Fireplace (Wood)', 'Other (specify)'];
const EXTRAS_OTHER_OPTS  = ['Other (specify)', 'Refer to comments', 'Refer to narrative addendum', 'Smart Home system (specify)'];
const EXTRAS_OTHER2_OPTS = ['Other (specify)'];
const OVERALL_COND       = ['Good', 'Average', 'Fair', 'Poor'];
const DETRIMENTAL_IMPR   = ['Asbestos', 'Damaged by (specify...)', 'Dirt Floor Basement', 'Foundation Cracks', 'Insulbrick', 'Knob and Tube Wiring', 'Lead/Kitec Plumbing', 'Moisture/Water Leak', 'Mould', 'Other (describe)', 'UFFI'];
const GARAGE_STALLS      = ['Single', 'Double', 'Triple', 'Single Attached', 'Single Detached', 'Double Attached', 'Double Detached', 'Triple Attached', 'Triple Detached', 'Not Applicable', 'Other (specify)'];
const GARAGE_DROP1       = ['Quad', 'Underground', 'Not Applicable', 'Other (specify)'];
const GARAGE_DROP2       = ['Carport (size)', 'Quonset', 'Second Garage (size)', 'Not Applicable', 'Other (specify)'];
const MEASUREMENT_SRCS   = ['Assessment Records', 'Building Plans', 'Measured', 'MLS', 'Online Data', 'Other (specify)'];

// ─── ROOM TABLE COLUMNS ────────────────────────────────────────────────────────

const ROOM_COLS = [
  ['entrance', 'ENT'], ['living', 'LIV'], ['dining', 'DIN'], ['kitchen', 'KIT'],
  ['family', 'FAM'], ['bedrooms', 'BED'], ['den', 'DEN'], ['full_bath', 'FB'],
  ['part_bath', 'PB'], ['laundry', 'LAU'], ['a', 'A'], ['b', 'B'], ['c', 'C'],
  ['room_total', 'TOTAL'], ['area', 'AREA'],
];

const ROOM_COUNT_KEYS = ['entrance','living','dining','kitchen','family','bedrooms','den','full_bath','part_bath','laundry','a','b','c'];

function computeRowTotal(row) {
  return ROOM_COUNT_KEYS.reduce((sum, k) => sum + (parseInt(row?.[k]) || 0), 0);
}

function sumColAcrossRows(rows, key) {
  if (key === 'room_total') {
    // Sum computed row totals, not stored values
    const t = rows.reduce((s, r) => s + computeRowTotal(r), 0);
    return t > 0 ? String(t) : '';
  }
  const t = rows.reduce((s, r) => s + (parseInt(r?.[key]) || 0), 0);
  return t > 0 ? String(t) : '';
}

function RoomRow({ label, data, onChange }) {
  const rowTotal = computeRowTotal(data);
  return (
    <tr>
      <td className="rt-room-label">{label}</td>
      {ROOM_COLS.map(([key]) => {
        if (key === 'room_total') {
          return (
            <td key={key}>
              <span className="rt-room-computed" style={{ display: 'block', textAlign: 'center', padding: '4px 2px' }}>
                {rowTotal > 0 ? rowTotal : ''}
              </span>
            </td>
          );
        }
        return (
          <td key={key}>
            <input
              className="rt-room-input"
              type="text"
              value={data?.[key] ?? ''}
              onChange={e => onChange({ ...data, [key]: e.target.value })}
            />
          </td>
        );
      })}
    </tr>
  );
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────

export default function ImprovementsTab({ improvements: impr, roomAllocation: ra, onChange, onRoomAllocationChange }) {
  const set   = (k, v) => onChange({ ...impr, [k]: v });
  const setIW = (k, v) => onChange({ ...impr, interior_walls:    { ...impr.interior_walls,    [k]: v } });
  const setIC = (k, v) => onChange({ ...impr, interior_ceilings: { ...impr.interior_ceilings, [k]: v } });
  const setBI = (k, v) => onChange({ ...impr, built_ins:         { ...impr.built_ins,         [k]: v } });
  const setEx = (k, v) => onChange({ ...impr, extras:            { ...impr.extras,            [k]: v } });
  const setGr = (k, v) => onChange({ ...impr, garage:            { ...impr.garage,            [k]: v } });
  const setRA = (k, v) => onRoomAllocationChange({ ...ra, [k]: v });

  const [showFourth,   setShowFourth]   = useState(!!(ra?.fourth?.room_total));
  const [showFifth,    setShowFifth]    = useState(!!(ra?.fifth?.room_total));
  const [showBasement2,setShowBasement2]= useState(!!(ra?.basement2?.room_total));

  const appendSiteImprovements = (text) => {
    const cur = impr.site_improvements || '';
    set('site_improvements', cur ? `${cur}\n\n${text}` : text);
  };
  const appendComments = (text) => {
    const cur = impr.comments || '';
    set('comments', cur ? `${cur}\n\n${text}` : text);
  };
  const appendBasement = (text) => {
    const cur = impr.basement_comments || '';
    set('basement_comments', cur ? `${cur}\n\n${text}` : text);
  };

  const agRows = [ra?.main, ra?.second, ra?.third];
  if (showFourth)  agRows.push(ra?.fourth);
  if (showFifth)   agRows.push(ra?.fifth);
  const bmRows = [ra?.basement];
  if (showBasement2) bmRows.push(ra?.basement2);

  return (
    <div className="rt-body">

      {/* ── A: BUILDING STATUS ── */}
      <div className="rt-section">
        <h3 className="rt-section-title">Building Status</h3>

        <label className="rt-check">
          <input type="checkbox" checked={impr.under_construction ?? false}
            onChange={e => set('under_construction', e.target.checked)} />
          Under Construction
        </label>

        {impr.under_construction && (
          <div style={{ marginTop: 12, paddingLeft: 8 }}>
            <div className="rt-check-group" style={{ marginBottom: 12 }}>
              <label className="rt-check">
                <input type="checkbox" checked={impr.appraised_as_is ?? true}
                  onChange={e => set('appraised_as_is', e.target.checked)} />
                Appraised As Is
              </label>
              <label className="rt-check">
                <input type="checkbox" checked={impr.as_if_complete ?? false}
                  onChange={e => set('as_if_complete', e.target.checked)} />
                As if Complete (new construction / renovation)
              </label>
            </div>
            <div className="rt-row">
              <div className="rt-field">
                <label>Construction Complete</label>
                <div className="rt-suffix-input">
                  <input type="text" value={impr.construction_complete_pct ?? ''}
                    onChange={e => set('construction_complete_pct', e.target.value)} />
                  <span className="rt-suffix">%</span>
                </div>
              </div>
              <div className="rt-field">
                <label>Additions Complete</label>
                <div className="rt-suffix-input">
                  <input type="text" value={impr.additions_complete_pct ?? ''}
                    onChange={e => set('additions_complete_pct', e.target.value)} />
                  <span className="rt-suffix">%</span>
                </div>
              </div>
            </div>
            <div className="rt-field">
              <label>Construction / Additions Comments</label>
              <textarea rows={3} value={impr.construction_comments ?? ''}
                onChange={e => set('construction_comments', e.target.value)} />
            </div>
          </div>
        )}
      </div>

      {/* ── B: BUILDING DETAILS ── */}
      <div className="rt-section">
        <h3 className="rt-section-title">Building Details</h3>

        <div className="rt-row">
          <div className="rt-field">
            <label>Year Built (est.)</label>
            <input type="text" value={impr.year_built ?? ''}
              onChange={e => set('year_built', e.target.value)} />
          </div>
          <div className="rt-field">
            <label>Year Additions (est.)</label>
            <input type="text" value={impr.year_additions ?? ''}
              onChange={e => set('year_additions', e.target.value)} />
          </div>
          <div className="rt-field">
            <label>Effective Age</label>
            <div className="rt-suffix-input">
              <input type="text" value={impr.effective_age ?? ''}
                onChange={e => set('effective_age', e.target.value)} />
              <span className="rt-suffix">yrs</span>
            </div>
          </div>
          <div className="rt-field">
            <label>Remaining Economic Life</label>
            <div className="rt-suffix-input">
              <input type="text" value={impr.remaining_economic_life ?? ''}
                onChange={e => set('remaining_economic_life', e.target.value)} />
              <span className="rt-suffix">yrs</span>
            </div>
          </div>
        </div>

        <div className="rt-row">
          <div className="rt-field">
            <label>Property Type</label>
            <select value={impr.property_type ?? ''}
              onChange={e => set('property_type', e.target.value)}>
              <option value="">Select…</option>
              {PROPERTY_TYPES.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="rt-field">
            <label>Design / Style</label>
            <input type="text" value={impr.design_style ?? ''}
              onChange={e => set('design_style', e.target.value)} />
          </div>
          <div className="rt-field">
            <label>Construction</label>
            <select value={impr.construction_type ?? ''}
              onChange={e => set('construction_type', e.target.value)}>
              <option value="">Select…</option>
              {CONSTRUCTION_TYPES.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
        </div>

        <div className="rt-row">
          <div className="rt-field">
            <label>Windows Type</label>
            <select value={impr.windows_type ?? ''}
              onChange={e => set('windows_type', e.target.value)}>
              <option value="">Select…</option>
              {WINDOW_TYPES.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="rt-field">
            <label>Windows Thickness</label>
            <select value={impr.windows_thickness ?? ''}
              onChange={e => set('windows_thickness', e.target.value)}>
              <option value="">Select…</option>
              {WINDOW_THICKNESS.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
        </div>

        <div className="rt-row">
          <div className="rt-field">
            <label>Basement</label>
            <select value={impr.basement_type ?? ''}
              onChange={e => set('basement_type', e.target.value)}>
              <option value="">Select…</option>
              {BASEMENT_TYPES.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="rt-field">
            <label>Basement Area</label>
            <div className="rt-suffix-input">
              <input type="text" value={impr.basement_area ?? ''}
                onChange={e => set('basement_area', e.target.value)} />
              <span className="rt-suffix">SqFt</span>
            </div>
          </div>
          <div className="rt-field">
            <label>Basement Finish</label>
            <div className="rt-suffix-input">
              <input type="text" value={impr.basement_finish_pct ?? ''}
                onChange={e => set('basement_finish_pct', e.target.value)} />
              <span className="rt-suffix">%</span>
            </div>
          </div>
          <div className="rt-field">
            <label>Foundation Walls</label>
            <select value={impr.foundation_walls ?? ''}
              onChange={e => set('foundation_walls', e.target.value)}>
              <option value="">Select…</option>
              {FOUNDATION_TYPES.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* ── C: EXTERIOR ── */}
      <div className="rt-section">
        <h3 className="rt-section-title">Exterior</h3>

        <div className="rt-row" style={{ alignItems: 'flex-end' }}>
          <div className="rt-field" style={{ flex: 2 }}>
            <label>Roofing</label>
            <select value={impr.roofing_type ?? ''}
              onChange={e => set('roofing_type', e.target.value)}>
              <option value="">Select…</option>
              {ROOFING_TYPES.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="rt-field rt-field-auto">
            <label>Condition</label>
            <div className="rt-check-group" style={{ marginTop: 4 }}>
              {EXT_CONDITIONS.map(c => (
                <label key={c} className="rt-check">
                  <input type="radio" checked={impr.roofing_condition === c}
                    onChange={() => set('roofing_condition', c)} />
                  {c}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="rt-field">
          <label>Roofing Notes</label>
          <input type="text" value={impr.roofing_notes ?? ''}
            onChange={e => set('roofing_notes', e.target.value)} />
        </div>

        <div className="rt-row" style={{ alignItems: 'flex-end', marginTop: 12 }}>
          <div className="rt-field" style={{ flex: 2 }}>
            <label>Exterior Finish</label>
            <select value={impr.exterior_finish ?? ''}
              onChange={e => set('exterior_finish', e.target.value)}>
              <option value="">Select…</option>
              {EXTERIOR_FINISHES.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="rt-field rt-field-auto">
            <label>Condition</label>
            <div className="rt-check-group" style={{ marginTop: 4 }}>
              {EXT_CONDITIONS.map(c => (
                <label key={c} className="rt-check">
                  <input type="radio" checked={impr.exterior_condition === c}
                    onChange={() => set('exterior_condition', c)} />
                  {c}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="rt-field">
          <label>Exterior Notes</label>
          <input type="text" value={impr.exterior_notes ?? ''}
            onChange={e => set('exterior_notes', e.target.value)} />
        </div>
      </div>

      {/* ── D: ENERGY & SYSTEMS ── */}
      <div className="rt-section">
        <h3 className="rt-section-title">Energy &amp; Systems</h3>

        <div className="rt-row">
          <div className="rt-field">
            <label>Energy Label</label>
            <select value={impr.energy_label ?? ''}
              onChange={e => set('energy_label', e.target.value)}>
              <option value="">Select…</option>
              {ENERGY_LABELS.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="rt-field">
            <label>Efficiency Rating</label>
            <input type="text" value={impr.energy_rating ?? ''}
              onChange={e => set('energy_rating', e.target.value)} />
          </div>
        </div>

        <div className="rt-row">
          <div className="rt-field">
            <label>EV Charger Type</label>
            <select value={impr.ev_charger ?? ''}
              onChange={e => set('ev_charger', e.target.value)}>
              <option value="">Select…</option>
              {EV_OPTIONS.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="rt-field">
            <label>Solar Panels</label>
            <select value={impr.solar_panels ?? ''}
              onChange={e => set('solar_panels', e.target.value)}>
              <option value="">Select…</option>
              {SOLAR_OPTIONS.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
        </div>

        <div className="rt-field">
          <label>Energy Comments</label>
          <input type="text" value={impr.energy_comments ?? ''}
            onChange={e => set('energy_comments', e.target.value)} />
        </div>
      </div>

      {/* ── E: MECHANICAL SYSTEMS ── */}
      <div className="rt-section">
        <h3 className="rt-section-title">Mechanical Systems</h3>

        <div className="rt-sub-label">Electrical</div>
        <div className="rt-row" style={{ alignItems: 'flex-end' }}>
          <div className="rt-field rt-field-auto">
            <div className="rt-check-group">
              <label className="rt-check">
                <input type="checkbox" checked={impr.electrical_fuses ?? false}
                  onChange={e => set('electrical_fuses', e.target.checked)} />
                Fuses
              </label>
              <label className="rt-check">
                <input type="checkbox" checked={impr.electrical_breakers ?? false}
                  onChange={e => set('electrical_breakers', e.target.checked)} />
                Breakers
              </label>
            </div>
          </div>
          <div className="rt-field">
            <label>Other</label>
            <select value={impr.electrical_other ?? ''}
              onChange={e => set('electrical_other', e.target.value)}>
              <option value="">Select…</option>
              {ELEC_OTHER_OPTS.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="rt-field">
            <label>Other 2</label>
            <select value={impr.electrical_other2 ?? ''}
              onChange={e => set('electrical_other2', e.target.value)}>
              <option value="">Select…</option>
              {ELEC_OTHER2_OPTS.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="rt-field">
            <label>Panel Capacity</label>
            <div className="rt-suffix-input">
              <input type="text" value={impr.panel_capacity ?? ''}
                onChange={e => set('panel_capacity', e.target.value)} />
              <span className="rt-suffix">amps</span>
            </div>
          </div>
        </div>

        <div className="rt-row" style={{ marginTop: 12 }}>
          <div className="rt-field">
            <label>Heating System</label>
            <select value={impr.heating_system ?? ''}
              onChange={e => set('heating_system', e.target.value)}>
              <option value="">Select…</option>
              {HEATING_SYSTEMS.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="rt-field">
            <label>Fuel Type</label>
            <select value={impr.fuel_type ?? ''}
              onChange={e => set('fuel_type', e.target.value)}>
              <option value="">Select…</option>
              {FUEL_TYPES.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
        </div>

        <div className="rt-row">
          <div className="rt-field">
            <label>Water Heater</label>
            <select value={impr.water_heater ?? ''}
              onChange={e => set('water_heater', e.target.value)}>
              <option value="">Select…</option>
              {WATER_HEATERS.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="rt-field">
            <label>Cooling System</label>
            <select value={impr.cooling_system ?? ''}
              onChange={e => set('cooling_system', e.target.value)}>
              <option value="">Select…</option>
              {COOLING_SYSTEMS.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* ── F: PLUMBING ── */}
      <div className="rt-section">
        <h3 className="rt-section-title">Plumbing</h3>
        <div className="rt-row">
          <div className="rt-field">
            <label>Plumbing Lines</label>
            <select value={impr.plumbing_lines ?? ''}
              onChange={e => set('plumbing_lines', e.target.value)}>
              <option value="">Select…</option>
              {PLUMBING_LINES.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="rt-field">
            <label>Info Source</label>
            <input type="text" value={impr.plumbing_source ?? ''}
              onChange={e => set('plumbing_source', e.target.value)} />
          </div>
        </div>
      </div>

      {/* ── G: INTERIOR FINISHES ── */}
      <div className="rt-section">
        <h3 className="rt-section-title">Interior Finishes</h3>

        <div className="rt-sub-label">Walls</div>
        <div className="rt-check-group" style={{ marginBottom: 16 }}>
          {[['drywall','Drywall'],['plaster','Plaster'],['paneling','Paneling']].map(([k,l]) => (
            <label key={k} className="rt-check">
              <input type="checkbox" checked={impr.interior_walls?.[k] ?? false}
                onChange={e => setIW(k, e.target.checked)} />
              {l}
            </label>
          ))}
        </div>

        <div className="rt-sub-label">Ceilings</div>
        <div className="rt-check-group" style={{ marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
          {[['drywall','Drywall'],['plaster','Plaster'],['paneling','Paneling']].map(([k,l]) => (
            <label key={k} className="rt-check">
              <input type="checkbox" checked={impr.interior_ceilings?.[k] ?? false}
                onChange={e => setIC(k, e.target.checked)} />
              {l}
            </label>
          ))}
          <div className="rt-field" style={{ flex: '0 0 180px' }}>
            <select value={impr.interior_ceilings?.other ?? ''}
              onChange={e => setIC('other', e.target.value)}>
              <option value="">Other…</option>
              {CEILING_OTHER_OPTS.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
        </div>

        <div className="rt-sub-label" style={{ marginTop: 8 }}>Flooring</div>
        <div className="rt-row">
          {['flooring1','flooring2','flooring3'].map((key, i) => (
            <div key={key} className="rt-field">
              <label>Flooring {i + 1}</label>
              <select value={impr[key] ?? ''}
                onChange={e => set(key, e.target.value)}>
                <option value="">Select…</option>
                {FLOORING_OPTIONS.map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
          ))}
        </div>
        <div className="rt-field">
          <label>Flooring Notes</label>
          <input type="text" value={impr.flooring_notes ?? ''}
            onChange={e => set('flooring_notes', e.target.value)} />
        </div>
      </div>

      {/* ── H: BUILT-INS & EXTRAS ── */}
      <div className="rt-section">
        <h3 className="rt-section-title">Built-ins &amp; Extras</h3>

        <div className="rt-sub-label">Built-in Appliances</div>
        <div className="rt-check-group" style={{ marginBottom: 16 }}>
          {[['stove','Stove'],['oven','Oven'],['dishwasher','Dishwasher'],
            ['microwave','Microwave'],['water_softener','Water Softener']].map(([k,l]) => (
            <label key={k} className="rt-check">
              <input type="checkbox" checked={impr.built_ins?.[k] ?? false}
                onChange={e => setBI(k, e.target.checked)} />
              {l}
            </label>
          ))}
        </div>

        <div className="rt-sub-label">Extras</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label className="rt-check">
            <input type="checkbox" checked={impr.extras?.security_system ?? false}
              onChange={e => setEx('security_system', e.target.checked)} />
            Security System
          </label>

          <div>
            <label className="rt-check">
              <input type="checkbox" checked={impr.extras?.fireplace ?? false}
                onChange={e => setEx('fireplace', e.target.checked)} />
              Fireplace
            </label>
            {impr.extras?.fireplace && (
              <select style={{ marginLeft: 24, marginTop: 6 }}
                value={impr.extras?.fireplace_type ?? ''}
                onChange={e => setEx('fireplace_type', e.target.value)}>
                <option value="">Select type…</option>
                {FIREPLACE_TYPES.map(o => <option key={o}>{o}</option>)}
              </select>
            )}
          </div>

          <label className="rt-check">
            <input type="checkbox" checked={impr.extras?.hr_ventilator ?? false}
              onChange={e => setEx('hr_ventilator', e.target.checked)} />
            HR/ER Ventilator
          </label>

          <label className="rt-check">
            <input type="checkbox" checked={impr.extras?.pool ?? false}
              onChange={e => setEx('pool', e.target.checked)} />
            Pool
          </label>

          <div>
            <label className="rt-check">
              <input type="checkbox" checked={impr.extras?.smart_home_enabled ?? false}
                onChange={e => setEx('smart_home_enabled', e.target.checked)} />
              Smart Home
            </label>
            {impr.extras?.smart_home_enabled && (
              <input type="text" style={{ marginLeft: 24, marginTop: 6 }}
                value={impr.extras?.smart_home ?? ''}
                placeholder="e.g. Nest, Ecobee…"
                onChange={e => setEx('smart_home', e.target.value)} />
            )}
          </div>

          <div className="rt-row" style={{ marginTop: 4 }}>
            <div className="rt-field">
              <label>Other</label>
              <select value={impr.extras?.other_drop ?? ''}
                onChange={e => setEx('other_drop', e.target.value)}>
                <option value="">Select…</option>
                {EXTRAS_OTHER_OPTS.map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div className="rt-field">
              <label>Other 2</label>
              <select value={impr.extras?.other_drop2 ?? ''}
                onChange={e => setEx('other_drop2', e.target.value)}>
                <option value="">Select…</option>
                {EXTRAS_OTHER2_OPTS.map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* ── I: OVERALL CONDITION ── */}
      <div className="rt-section">
        <h3 className="rt-section-title">Overall Condition</h3>

        <div className="rt-field">
          <label>Overall Interior Condition</label>
          <div className="rt-check-group" style={{ marginTop: 4 }}>
            {OVERALL_COND.map(o => (
              <label key={o} className="rt-check">
                <input type="radio" checked={impr.overall_condition === o}
                  onChange={() => set('overall_condition', o)} />
                {o}
              </label>
            ))}
          </div>
        </div>

        <div className="rt-field" style={{ marginTop: 12 }}>
          <label>Source of Interior Information</label>
          <input type="text" value={impr.source_of_interior ?? ''}
            onChange={e => set('source_of_interior', e.target.value)} />
        </div>

        <div className="rt-field" style={{ marginTop: 12 }}>
          <label className="rt-check">
            <input type="checkbox" checked={impr.detrimental_observed ?? false}
              onChange={e => {
                set('detrimental_observed', e.target.checked);
                if (!e.target.checked) set('detrimental_type', '');
              }} />
            Detrimental Conditions Observed
          </label>
          {impr.detrimental_observed && (
            <div style={{ marginTop: 8 }}>
              <select value={impr.detrimental_type ?? ''}
                onChange={e => set('detrimental_type', e.target.value)}>
                <option value="">Select…</option>
                {DETRIMENTAL_IMPR.map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* ── J: ROOM ALLOCATION ── */}
      <div className="rt-section">
        <h3 className="rt-section-title">Room Allocation</h3>

        <div className="rt-room-table-wrapper">
          <table className="rt-room-table">
            <thead>
              <tr>
                <th className="rt-room-label-th">Level</th>
                {ROOM_COLS.map(([, abbr]) => <th key={abbr}>{abbr}</th>)}
              </tr>
            </thead>
            <tbody>
              <RoomRow label="Main"   data={ra?.main}   onChange={v => setRA('main', v)} />
              <RoomRow label="Second" data={ra?.second} onChange={v => setRA('second', v)} />
              <RoomRow label="Third"  data={ra?.third}  onChange={v => setRA('third', v)} />
              {showFourth && (
                <RoomRow label="4th" data={ra?.fourth} onChange={v => setRA('fourth', v)} />
              )}
              {showFifth && (
                <RoomRow label="5th" data={ra?.fifth}  onChange={v => setRA('fifth', v)} />
              )}
              <RoomRow label="Basement"  data={ra?.basement}  onChange={v => setRA('basement', v)} />
              {showBasement2 && (
                <RoomRow label="Bsmt 2" data={ra?.basement2} onChange={v => setRA('basement2', v)} />
              )}
            </tbody>
            <tfoot>
              {[['AG Tot', agRows], ['Bsmt', bmRows]].map(([lbl, rows]) => (
                <tr key={lbl} className="rt-room-total-row">
                  <td className="rt-room-label">{lbl}</td>
                  {ROOM_COLS.map(([key]) => (
                    <td key={key}><span className="rt-room-computed">{sumColAcrossRows(rows, key)}</span></td>
                  ))}
                </tr>
              ))}
            </tfoot>
          </table>
        </div>

        <div className="rt-room-toggle-row">
          <button className="rt-room-toggle-btn" type="button"
            onClick={() => setShowFourth(v => !v)}>
            {showFourth ? '− Hide 4th Floor' : '+ Add 4th Floor'}
          </button>
          <button className="rt-room-toggle-btn" type="button"
            onClick={() => setShowFifth(v => !v)}>
            {showFifth ? '− Hide 5th Floor' : '+ Add 5th Floor'}
          </button>
          <button className="rt-room-toggle-btn" type="button"
            onClick={() => setShowBasement2(v => !v)}>
            {showBasement2 ? '− Hide Basement 2' : '+ Add Basement 2'}
          </button>
        </div>

        <div className="rt-row" style={{ marginTop: 16 }}>
          <div className="rt-field">
            <label>Source of Measurement</label>
            <select value={impr.source_of_measurement ?? ''}
              onChange={e => set('source_of_measurement', e.target.value)}>
              <option value="">Select…</option>
              {MEASUREMENT_SRCS.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="rt-field rt-field-auto">
            <label>Unit of Measurement</label>
            <div className="rt-check-group" style={{ marginTop: 4 }}>
              {['SqFt','SqM'].map(u => (
                <label key={u} className="rt-check">
                  <input type="radio" checked={(impr.unit_of_measurement ?? 'SqFt') === u}
                    onChange={() => set('unit_of_measurement', u)} />
                  {u}
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── K: GARAGE / PARKING ── */}
      <div className="rt-section">
        <h3 className="rt-section-title">Garage / Parking</h3>

        <div className="rt-check-group" style={{ marginBottom: 12 }}>
          {[['attached','Attached'],['detached','Detached'],['built_in','Built-in']].map(([k,l]) => (
            <label key={k} className="rt-check">
              <input type="checkbox" checked={impr.garage?.[k] ?? false}
                onChange={e => setGr(k, e.target.checked)} />
              {l}
            </label>
          ))}
        </div>

        <div className="rt-row">
          <div className="rt-field">
            <label>Stalls</label>
            <select value={impr.garage?.stalls ?? ''}
              onChange={e => setGr('stalls', e.target.value)}>
              <option value="">Select…</option>
              {GARAGE_STALLS.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="rt-field">
            <label>Additional</label>
            <select value={impr.garage?.drop1 ?? ''}
              onChange={e => setGr('drop1', e.target.value)}>
              <option value="">Select…</option>
              {GARAGE_DROP1.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="rt-field">
            <select value={impr.garage?.drop2 ?? ''}
              onChange={e => setGr('drop2', e.target.value)}>
              <option value="">Select…</option>
              {GARAGE_DROP2.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="rt-field">
            <select value={impr.garage?.drop3 ?? ''}
              onChange={e => setGr('drop3', e.target.value)}>
              <option value="">Select…</option>
              {GARAGE_DROP2.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* ── L: COMMENTS ── */}
      <div className="rt-section">
        <h3 className="rt-section-title">Comments</h3>

        <div className="rt-field">
          <div className="rt-field-label-row">
            <label>Site Improvements</label>
            <ResponsePicker category="site" onInsert={appendSiteImprovements} />
          </div>
          <textarea rows={3} value={impr.site_improvements ?? ''}
            onChange={e => set('site_improvements', e.target.value)} />
        </div>

        <div className="rt-field" style={{ marginTop: 10 }}>
          <div className="rt-field-label-row">
            <label>Building Comments</label>
            <ResponsePicker category="improvements" onInsert={appendComments} />
          </div>
          <textarea rows={6} value={impr.comments ?? ''}
            onChange={e => set('comments', e.target.value)} />
        </div>

        <div className="rt-field" style={{ marginTop: 10 }}>
          <div className="rt-field-label-row">
            <label>Basement Comments</label>
            <ResponsePicker category="improvements" onInsert={appendBasement} />
          </div>
          <textarea rows={3} value={impr.basement_comments ?? ''}
            onChange={e => set('basement_comments', e.target.value)} />
        </div>
      </div>

    </div>
  );
}
